import { ObjectContentNode } from '@helo/cms'
import { DataTypeConfigUtils } from '@helo/cms'
import { pipe } from '@helo/utils'
import { Array } from 'effect'

import { showToast } from '../../components/Toast/showToast'
import { ExportColumn } from '../../services/queryService'
import { useAppStore } from '../../store/appStore/appStore'
import { RouterSlice } from '../../store/appStore/router/routerSlice'
import {
  SavedView,
  ViewQueryState,
  WidgetState,
} from '../../store/appStore/view/viewSlice.types'
import { SavedColumnState } from '../../widgets/TableWidget/tablewidget.parts'

export type SavedViewWithLabel = SavedView & {
  viewLabel: string
}

export const exists = <T,>(value: T): value is Exclude<T, undefined | null> =>
  value !== undefined && value !== null

export const addViewLabel =
  (store: RouterSlice) =>
  (view: SavedView): SavedViewWithLabel => ({
    ...view,
    viewLabel: store.router.getRouteById(view.viewId)?.label ?? view.name,
  })

export const prepareViews = (savedViews: SavedView[]): SavedViewWithLabel[] =>
  pipe(savedViews, Array.map(addViewLabel(useAppStore.getState())))

export const handleShare = async (savedView: ObjectContentNode) => {
  const nameField = pipe(savedView, DataTypeConfigUtils.find([['key', 'name']]))

  if (!nameField) throw new Error('Name field not found')

  const baseUrl = nameField.state.linkUrl
  const savedViewId = nameField.state.linkUrlStorage?.savedViewId

  if (!baseUrl) throw new Error('URL not found')

  // Construct URL with savedViewId query parameter
  const url = savedViewId ? `${baseUrl}?savedViewId=${savedViewId}` : baseUrl

  const link = `${window.location.origin}${url}`

  try {
    await navigator.clipboard.writeText(link)

    showToast({
      title: 'Share view link',
      description: 'The link was copied to your clipboard.',
      icon: 'CopyPasteIcon',
    })
  } catch (err) {
    console.error(err)
    showToast({
      title: 'Share view link',
      description: 'Unable to copy link to your clipboard.',
      icon: 'CopyPasteIcon',
      variant: 'error',
    })
  }
}

export const getColumnsToExport = (
  widgetState: WidgetState,
  queryState: ViewQueryState,
): ExportColumn[] => {
  const state = widgetState?.state as {
    _tableColumns: SavedColumnState[] | undefined
  }

  const queryDefinition = useAppStore
    .getState()
    .queryStore.getQuery(queryState.queryId)?.queryDefinition

  if (!queryDefinition) return []

  const columns = state?._tableColumns || queryDefinition.fields

  const exportColumns = columns
    .map((column): ExportColumn | undefined => {
      const field = queryDefinition.fields.find(
        field => field.key === column.key,
      )

      if (!field) return undefined

      return {
        field: field.key,
        heading: field.label || field.key,
        typeName: field.type,
      }
    })
    .filter(item => !!item)

  return exportColumns
}
